<script setup lang="ts">
/**
 * EnvironmentIndicator Component
 *
 * Displays current environment (MOCK/LOCAL/DEV/TEST/PROD).
 * - Expanded: floating panel with BFF, API, Auth0, Mode details
 * - Collapsed: inline badge in footer next to version number
 *
 * Environment detection (from browser URL):
 * - MOCK: VITE_ENABLE_MOCK_SERVICES=true
 * - LOCAL: localhost / 127.0.0.1
 * - DEV: hostname contains dev.sitecompass.net
 * - TEST: hostname contains test.sitecompass.net
 * - PROD: hostname contains sitecompass.net (catch-all)
 *
 * Database label is fetched from the BFF /health endpoint.
 *
 * Color coding: Blue=DEV/LOCAL, Yellow=TEST, Red=PROD, Purple=MOCK, Gray=UNKNOWN
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useUserPreferencesStore } from '@stores/user-preferences.store'

const prefs = useUserPreferencesStore()

const useMockServices = import.meta.env.VITE_ENABLE_MOCK_SERVICES === 'true'

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''

// Derive the full BFF origin URL (strip /api suffix).
// On AWS the app is served by the BFF itself, so fall back to the current window origin.
const bffOrigin = apiBaseUrl ? apiBaseUrl.replace(/\/api\/?$/, '') : window.location.origin

// BFF health data (database label)
const bffDatabaseLabel = ref<string>('')
const bffHealthFetched = ref(false)

// Collapse state — initialised from the stored user preference
const isCollapsed = ref(prefs.infoDialogDefaultCollapsed)

// Keep collapse state in sync if the user changes their default in the profile dialog
watch(
  () => prefs.infoDialogDefaultCollapsed,
  collapsed => {
    isCollapsed.value = collapsed
  }
)

const fetchBffEnvironment = async () => {
  if (useMockServices) {
    bffHealthFetched.value = true
    return
  }
  try {
    // Always hit the BFF directly — Vite proxy only forwards /api/*, not /health
    const response = await fetch(`${bffOrigin}/health`)
    if (response.ok) {
      const data = (await response.json()) as Record<string, unknown>
      const db = data['database'] as Record<string, unknown> | undefined
      if (db && typeof db['label'] === 'string') {
        bffDatabaseLabel.value = db['label']
      }
    }
  } catch {
    // Silently fail — environment detection is best-effort
  } finally {
    bffHealthFetched.value = true
  }
}

onMounted(() => {
  fetchBffEnvironment()
})

const COLOR_MAP: Record<string, string> = {
  MOCK: 'bg-purple-600',
  LOCAL: 'bg-blue-600',
  DEV: 'bg-red-600',
  TEST: 'bg-yellow-500',
  PROD: 'bg-brand-400',
  UNKNOWN: 'bg-charcoal-600',
}

const TEXT_COLOR_MAP: Record<string, string> = {
  MOCK: 'text-purple-400',
  LOCAL: 'text-blue-400',
  DEV: 'text-red-400',
  TEST: 'text-yellow-400',
  PROD: 'text-brand-400',
  UNKNOWN: 'text-charcoal-400',
}

/** Derive mode from the browser URL — this is the authoritative source */
function detectMode(): string {
  if (useMockServices) return 'MOCK'

  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'LOCAL'
  if (hostname.includes('dev.sitecompass.net')) return 'DEV'
  if (hostname.includes('test.sitecompass.net')) return 'TEST'
  if (hostname.includes('sitecompass.net')) return 'PROD'
  return 'UNKNOWN'
}

const mode = detectMode()

const environment = computed(() => {
  const color = COLOR_MAP[mode] ?? COLOR_MAP['UNKNOWN']
  const textColor = TEXT_COLOR_MAP[mode] ?? TEXT_COLOR_MAP['UNKNOWN']

  return { name: mode, color, textColor }
})

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || 'Not configured'

/**
 * Build identity, baked into the bundle at build time by the deploy workflow:
 * VITE_BUILD_NUMBER = github.run_number, VITE_BUILD_SHA = github.sha.
 * Because these are compiled into the bundle (not read from the BFF), the value
 * shown is exactly the bundle running in the browser — so a changed stamp proves
 * the new frontend code is actually live, not a stale cached one.
 * Both unset in local dev → "local".
 */
const buildStamp = computed(() => {
  const num = import.meta.env.VITE_BUILD_NUMBER
  const sha = import.meta.env.VITE_BUILD_SHA
  const shortSha = sha ? sha.slice(0, 7) : ''
  if (num && shortSha) return `${num} · ${shortSha}`
  return num || shortSha || 'local'
})

/** Derive a text color class based on the database label */
const dbLabelColor = computed(() => {
  const label = bffDatabaseLabel.value
  if (!label || label === 'Loading...' || label === 'Unknown') return 'text-charcoal-400'
  if (label === 'Local (Docker)') return 'text-blue-400'
  if (label === 'AWS Dev') return 'text-red-400'
  if (label === 'AWS Test') return 'text-yellow-400'
  if (label === 'AWS Prod') return 'text-brand-400'
  return 'text-white'
})

const toggle = () => {
  isCollapsed.value = !isCollapsed.value
}
</script>

<template>
  <!-- Collapsed: inline badge teleported into footer next to version -->
  <Teleport to="#env-indicator-slot">
    <Transition name="env-fade">
      <button
        v-if="prefs.infoDialogVisible && isCollapsed"
        type="button"
        :class="[
          'inline-flex items-center font-mono text-sm font-bold transition-colors duration-200 hover:text-white cursor-pointer leading-none',
          environment.textColor,
        ]"
        title="Click to expand environment info"
        @click="toggle"
      >
        <span class="text-charcoal-300 mx-1">|</span>
        {{ environment.name }}
      </button>
    </Transition>
  </Teleport>

  <!-- Expanded: floating panel -->
  <Transition name="env-panel">
    <div
      v-if="prefs.infoDialogVisible && !isCollapsed"
      class="fixed right-4 bottom-24 z-50 rounded-lg shadow-lg border border-white/20 overflow-hidden bg-charcoal-900 max-w-xs"
    >
      <!-- Title bar: click anywhere to collapse -->
      <div
        :class="[
          environment.color,
          'flex items-center justify-between gap-2 px-3 py-1.5 cursor-pointer select-none',
        ]"
        title="Click to collapse"
        @click="toggle"
      >
        <span class="text-white font-mono text-xs font-bold whitespace-nowrap">Info</span>
        <span class="flex-shrink-0 text-white/80 text-[10px] leading-none">▼</span>
      </div>

      <!-- Details panel -->
      <div class="text-white text-xs font-mono space-y-1.5 p-3">
        <div>
          <span class="text-charcoal-400">BFF:</span>
          <span class="ml-2 text-white break-all">{{ bffOrigin }}</span>
        </div>
        <div v-if="!useMockServices">
          <span class="text-charcoal-400">Auth0:</span>
          <span class="ml-2 text-white break-all">{{ auth0Domain }}</span>
        </div>
        <div>
          <span class="text-charcoal-400">Mode:</span>
          <span class="ml-2 text-white">{{ environment.name }}</span>
        </div>
        <div>
          <span class="text-charcoal-400">Database:</span>
          <span :class="['ml-2', dbLabelColor]">{{
            bffDatabaseLabel || (bffHealthFetched ? 'Unknown' : 'Loading...')
          }}</span>
        </div>
        <div>
          <span class="text-charcoal-400">Build:</span>
          <span class="ml-2 text-white break-all">{{ buildStamp }}</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Panel slide up/down */
.env-panel-enter-active,
.env-panel-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.env-panel-enter-from,
.env-panel-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* Inline badge fade */
.env-fade-enter-active,
.env-fade-leave-active {
  transition: opacity 0.2s ease;
}
.env-fade-enter-from,
.env-fade-leave-to {
  opacity: 0;
}
</style>
